/**
 * Chat bubble side:
 * - right / blue = "my side" for the current viewer + view context
 * - left / gray  = the other side
 *
 * My Cases: always own messages right, everyone else left.
 * Admin Cases (internal roles): internal-workflow messages right, rest left —
 *   except when viewing a case you own, then own left and everyone else right.
 * Admin Cases (Side Admin): lab / partner-lab messages right, internal left.
 */

export type ChatViewContext = "my-cases" | "admin-cases";

const INTERNAL_WORKFLOW_ROLES = new Set([
  "super admin",
  "admin",
  "admin read",
  "operations",
  "production",
]);

const INTERNAL_MESSAGE_TYPES = new Set([
  "super admin_comment",
  "admin_comment",
  "admin read_comment",
  "operations_comment",
  "production_comment",
  "crm_comment",
]);

const LAB_SIDE_MESSAGE_TYPES = new Set([
  "user_comment",
  "side admin_comment",
  "custom_comment",
]);

function normalize(value?: string | null): string {
  return value?.toLowerCase()?.trim() || "";
}

export function isSystemChatMessage(type?: string | null): boolean {
  const t = normalize(type);
  return t === "system" || t === "system_update" || t === "redesign_update";
}

export function isInternalWorkflowRole(role?: string | null): boolean {
  return INTERNAL_WORKFLOW_ROLES.has(normalize(role));
}

function roleFromCommentType(type: string): string {
  return type.endsWith("_comment") ? type.slice(0, -"_comment".length) : "";
}

export function isInternalWorkflowMessage(type?: string | null): boolean {
  const t = normalize(type);
  if (!t || isSystemChatMessage(t)) return false;
  if (INTERNAL_MESSAGE_TYPES.has(t)) return true;
  return isInternalWorkflowRole(roleFromCommentType(t));
}

export function isLabSideMessage(type?: string | null): boolean {
  const t = normalize(type);
  if (!t || isSystemChatMessage(t)) return false;
  if (LAB_SIDE_MESSAGE_TYPES.has(t)) return true;
  const role = roleFromCommentType(t);
  return role === "user" || role === "side admin" || role === "custom";
}

function sameId(
  a?: number | string | null,
  b?: number | string | null,
): boolean {
  if (a == null || b == null) return false;
  return Number(a) === Number(b);
}

export type ChatSideOptions = {
  myUserId?: number | string | null;
  myRole?: string | null;
  viewContext: ChatViewContext;
  /** Cases.user_id — needed for Admin Cases alignment */
  caseOwnerUserId?: number | string | null;
};

/**
 * True → render on the right (blue). False → left (gray).
 * System messages should be centered by the caller; this returns false for them.
 */
export function isChatMessageOnRight(
  msg: {
    user_id?: number | string | null;
    type?: string | null;
  },
  opts: ChatSideOptions,
): boolean {
  if (isSystemChatMessage(msg.type)) return false;

  const isOwn = sameId(msg.user_id, opts.myUserId);
  const roleLower = normalize(opts.myRole);

  // My Cases — user, side admin own cases, or internal viewing own case
  if (opts.viewContext === "my-cases") {
    return isOwn;
  }

  // Admin Cases — Side Admin stands with the partner lab
  if (roleLower === "side admin") {
    if (isLabSideMessage(msg.type)) return true;
    if (sameId(msg.user_id, opts.caseOwnerUserId)) return true;
    return false;
  }

  // Admin Cases — internal workflow roles
  if (isInternalWorkflowRole(opts.myRole)) {
    const viewingOwnCase = sameId(opts.caseOwnerUserId, opts.myUserId);

    // Internal-created case opened from Admin Cases:
    // own messages left gray, everyone else right blue
    if (viewingOwnCase) {
      return !isOwn;
    }

    // Normal admin view: internal right, lab/user left
    return isInternalWorkflowMessage(msg.type);
  }

  // Fallback (e.g. User on an admin route)
  return isOwn;
}

/** @deprecated Prefer isChatMessageOnRight with view context */
export function isMyChatMessage(
  msg: {
    user_id?: number | string | null;
    type?: string | null;
  },
  myUserId?: number | string | null,
): boolean {
  return isChatMessageOnRight(msg, {
    myUserId,
    viewContext: "my-cases",
  });
}
